import { NextRouter } from 'next/router'
import {
    QaBrowserRequest,
    QaBrowserRequestKind,
    QaBrowserResponse,
} from 'common/src/api/backendToBackend/qa/types'

// Executes agent → browser requests for creator conversations (see
// Content-Backend src/mcp/browserTools.ts). Mirrors Website-Frontend's
// utils/agentBrowserRequests.ts (keep in sync).

const MAX_HTML_CHARS = 160_000

export const captureCurrentPageHtml = (): string => {
    const clone = document.documentElement.cloneNode(true) as HTMLElement
    clone
        .querySelectorAll(
            'script, style, link, noscript, svg, iframe, canvas, template, .ant-modal-root, .ant-notification, .ant-message, [data-agent-ignore]'
        )
        .forEach((node) => node.remove())
    clone.querySelectorAll('*').forEach((element) => {
        for (const attribute of Array.from(element.attributes)) {
            const name = attribute.name
            if (
                name === 'class' ||
                name === 'style' ||
                name === 'nonce' ||
                name.startsWith('on') ||
                (name.startsWith('data-') && name !== 'data-testid')
            )
                element.removeAttribute(name)
        }
        if (
            element.tagName === 'INPUT' &&
            /password|hidden/i.test(element.getAttribute('type') ?? '')
        )
            element.remove()
    })
    let html = clone.outerHTML
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/>\s+</g, '><')
        .replace(/\s{2,}/g, ' ')
    if (html.length > MAX_HTML_CHARS)
        html = `${html.slice(0, MAX_HTML_CHARS)}\n<!-- truncated: page HTML exceeded ${MAX_HTML_CHARS} chars -->`
    return html
}

export const toSameOriginPath = (url: string): string | null => {
    const trimmed = (url ?? '').trim()
    if (!trimmed || trimmed.startsWith('//')) return null
    if (trimmed.startsWith('/')) return trimmed
    try {
        const parsed = new URL(trimmed, window.location.origin)
        if (parsed.origin !== window.location.origin) return null
        return `${parsed.pathname}${parsed.search}${parsed.hash}`
    } catch {
        return null
    }
}

const now = (): string => new Date().toISOString()

export const executeAgentBrowserRequest = async (
    request: QaBrowserRequest,
    router: NextRouter
): Promise<QaBrowserResponse> => {
    const base = { browserRequestId: request.browserRequestId, at: now() }
    if (request.kind === QaBrowserRequestKind.getCurrentPage) {
        return {
            ...base,
            ok: true,
            url: window.location.href,
            html: captureCurrentPageHtml(),
            error: null,
        }
    }
    if (request.kind === QaBrowserRequestKind.navigate) {
        const target = toSameOriginPath(request.url ?? '')
        if (!target)
            return {
                ...base,
                ok: false,
                url: window.location.href,
                html: null,
                error: 'Only pages on this site can be opened.',
            }
        try {
            await router.push(target)
            return {
                ...base,
                ok: true,
                url: window.location.href,
                html: null,
                error: null,
            }
        } catch (error) {
            return {
                ...base,
                ok: false,
                url: window.location.href,
                html: null,
                error: `Navigation failed: ${String(error)}`,
            }
        }
    }
    return {
        ...base,
        ok: false,
        url: window.location.href,
        html: null,
        error: `Unknown request kind: ${String(request.kind)}`,
    }
}
